
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DOCUMENT, ElementRef, OnDestroy, Renderer2, ViewChild, WritableSignal, computed, effect, inject, input, output, signal } from '@angular/core';
import { CarouselComponent, CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { CardLoaderComponent } from "src/app/components/card-loader/card-loader.component";
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { roundOff } from 'src/app/core/utilities/number-formater.utility';
import { CUSTOM_OPTIONS } from 'src/app/home/components/dynamic-template/dynamic-template.component';
import { ProductListingVariant } from 'src/app/products/models/product-listing.model';
import { Collection } from 'src/app/products/models/product.model';
import { ProductsRepositoryService } from 'src/app/products/services/products-repository.service';
import { SafehtmlPipe } from 'src/app/shared/pipes/safehtml.pipe';
import { DynamicHomeProductCardComponent } from "../dynamic-home-product-card/dynamic-home-product-card.component";

export interface CollectionListData {
  collections: Collection[];
  [key: number]: ProductListingVariant[];
}

@Component({
  standalone: true,
  selector: 'webstore-multiple-collection-product-list',
  templateUrl: './multiple-collection-product-list.component.html',
  styleUrls: ['./multiple-collection-product-list.component.scss'],
  imports: [
    CommonModule,
    CarouselModule,
    DynamicHomeProductCardComponent,
    CardLoaderComponent,
    SafehtmlPipe
  ]
})
export class MultipleCollectionProductListComponent implements AfterViewInit, OnDestroy {
  private productsRepositoryService = inject(ProductsRepositoryService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private elementRef = inject(ElementRef);
  private document = inject<Document>(DOCUMENT);
  private renderer = inject(Renderer2);
  private readonly deviceDetectionService = inject(DeviceDetectionService);
  private updateSectionTimeoutIds: Array<ReturnType<typeof setTimeout>> = [];

  @ViewChild('carouselListDiv') section!: ElementRef;
  @ViewChild('multipleCollectionCarouselList') carouselList!: CarouselComponent;

  readonly customOptions = input<OwlOptions>(CUSTOM_OPTIONS);
  readonly carouselListNavigationSection = input<string>('');
  readonly baseHtml = input<string>('');
  readonly sectionId = input<string>('');
  readonly countDownTemplate = input<boolean>(false);
  readonly showCashpoint = input<boolean>(false);
  readonly showPercentageDiscount = input<boolean>(true);
  readonly showAddToCart = input<boolean>(false);

  // Expose device detection service signals for template usage
  readonly isMobile = this.deviceDetectionService.isMobile;
  readonly isDesktop = this.deviceDetectionService.isDesktop;
  readonly isTablet = this.deviceDetectionService.isTablet;
  readonly deviceType = this.deviceDetectionService.deviceType;

  readonly collectionIds = input<string[]>([]);

  private _collectionIds: string[] = [];

  // Update outputs to use Angular 20 output() function
  addToCartClicked$ = output<boolean>();
  productClicked$ = output<string>();

  constructor() {
    // Effect to handle collectionIds changes
    effect(() => {
      const ids = this.collectionIds();
      if (ids?.length) {
        this.loader.set(true);
        this._collectionIds = ids.filter(id => id && typeof id === 'string');
        this.configureCollectionListData();
      }
    });

    // Effect to handle sectionId changes and update class names
    effect(() => {
      const currentSectionId = this.sectionId();
      if (currentSectionId) {
        // Use setTimeout to ensure DOM is ready and change detection has completed
        const timeoutId = setTimeout(() => {
          this.updateSectionIdAndClass();
        }, 0);
        this.updateSectionTimeoutIds.push(timeoutId);
      }
    });
  }

  onAddToCartClicked(event: boolean) {
    this.addToCartClicked$.emit(event);
  }

  onProductClicked(urlPath: string) {
    this.productClicked$.emit(urlPath);
  }

  // Signal-based state management
  private _selectedIndex = signal<number>(0);
  readonly selectedIndex = this._selectedIndex.asReadonly();

  collectionList: any;

  // Signal-based state management
  private _collectionListData = signal<CollectionListData>({
    collections: []
  });
  readonly collectionListData = this._collectionListData.asReadonly();

  private _productFetchState = signal<{ [key: number]: boolean }>({});
  readonly productFetchState = this._productFetchState.asReadonly();

  private _countDownPresent = signal<boolean>(false);
  readonly countDownPresentSignal = this._countDownPresent.asReadonly();

  // Computed signal to get selected products
  readonly selectedProducts = computed(() => {
    const data = this._collectionListData();
    const index = this.selectedIndex();
    return data[index];
  });


  loader: WritableSignal<boolean> = signal(false);
  showOutOfStockProducts = this.storeConfigurationRepositoryService.showOutOfStockProducts;

  ngAfterViewInit(): void {
    // Initial setup
    this.updateSectionIdAndClass();

    // Add click actions to carousel navigation
    this.addClickActionsToCarouselList();
  }


  private updateSectionIdAndClass(): void {
    if (this.section?.nativeElement) {
      const currentSectionId = this.sectionId();
      if (currentSectionId) {
        // Update the section ID
        this.section.nativeElement.id = currentSectionId;

        // Find the section element and add/update the style class
        const sectionToAddClass = this.document.getElementById(currentSectionId);
        if (sectionToAddClass) {
          // Remove any existing style classes first
          const existingStyleClasses = Array.from(sectionToAddClass.classList)
            .filter(cls => cls.startsWith('style-'));
          existingStyleClasses.forEach(cls => sectionToAddClass.classList.remove(cls));

          // Add the new style class
          sectionToAddClass.classList.add(`style-${currentSectionId}`);
        }
      }
    }
  }

  async configureCollectionListData() {
    this.loader.set(true);

    try {
      // Step 1: Fetch all collection details and store them individually
      const validCollections: any[] = [];

      for (let i = 0; i < this._collectionIds.length; i++) {
        const collectionId = this._collectionIds[i];
        try {
          await this.productsRepositoryService.getCollectionDetails(collectionId ?? '');

          // Get the collection immediately after fetching (before it gets overwritten)
          const collection = this.productsRepositoryService.collection();
          if (collection) {
            validCollections.push(collection);
          }
        } catch (error) {
        }
      }

      // Step 2: Update collections in signal
      this._collectionListData.update(data => ({
        ...data,
        collections: validCollections
      }));


      // Step 4: For each valid collection, fetch products
      for (let newIndex = 0; newIndex < validCollections.length; newIndex++) {
        const collectionId = this._collectionIds[newIndex];
        if (!collectionId) continue;

        const params = {
          currentFilters: {
            collection_ids: collectionId,
            ...(!this.showOutOfStockProducts() && { in_stock: true })
          },
          pageNumber: 0,
          pageSize: 1000,
          search: '',
          sortActive: '',
          sortDirection: '',
          ordering: '-created_at'
        };

        try {
          await this.productsRepositoryService.getProducts(params);

          // Get products from the repository signal IMMEDIATELY after the call
          const productResponse = this.productsRepositoryService.products();
          if (productResponse?.hits?.hits) {
            const products = productResponse.hits.hits.map(hit => hit._source).map((product: any) => {
              product.mrp = typeof (product?.mrp) === 'string' ? product?.mrp : roundOff(product?.mrp ?? 0) as number;
              // Fix: Use selling_price directly instead of price_list path
              product.selling_price = roundOff(product?.selling_price ?? 0) as number;
              // Add missing properties to match ProductListingVariant interface
              product.is_grouped_product = product.is_grouped_product ?? false;
              product.price_for_everyone = product.price_for_everyone ?? 0;
              // Set stock properties for add-to-cart functionality
              product.in_stock = product?.in_stock ?? true;
              product.is_allow_back_orders = product?.is_allow_back_orders ?? false;
              return product;
            });

            // Update product fetch state
            this._productFetchState.update(state => ({
              ...state,
              [newIndex]: true
            }));

            // Update collection list data with products
            this._collectionListData.update(data => ({
              ...data,
              [newIndex]: products
            }));


            // Update countdown present state
            this._countDownPresent.set(products?.some((product: any) => !!product?.deal) || false);

            // Add click actions to carousel after data is loaded (crucial for carousel initialization)
            this.addClickActionsToCarouselList();
          }
        } catch (error) {
          // Set empty array for failed collection
          this._collectionListData.update(data => ({
            ...data,
            [newIndex]: []
          }));
        }
      }

      this.changeSelectedIndex(0);
    } catch (error) {
    } finally {
      this.loader.set(false);
    }
  }

  changeSelectedIndex(index: number) {
    if (index === this.selectedIndex())
      return;

    if (!this.productFetchState()[index]) {
      this.loader.set(true);
      // this.cdr.detectChanges();
    }
    this._selectedIndex.set(index);
  }

  addClickActionsToCarouselList() {
    this.setupEventDelegation();
    this.setupDirectClickListeners();
  }

  private setupEventDelegation() {
    this.renderer.listen(this.elementRef.nativeElement, 'click', (event: Event) => {
      const target = event.target as Element;

      if (target.matches && target.matches('.carousel-list-nav-prev, .carousel-list-nav-next')) {
        event.preventDefault();
        event.stopPropagation();

        if (target.classList.contains('carousel-list-nav-prev')) {
          this.carouselList.prev();
        } else if (target.classList.contains('carousel-list-nav-next')) {
          this.carouselList.next();
        }
      }
    });
  }

  private setupDirectClickListeners() {
    const findElements = () => {
      const previousSlideElement = this.elementRef.nativeElement.querySelector('.carousel-list-nav-prev');
      const nextSlideElement = this.elementRef.nativeElement.querySelector('.carousel-list-nav-next');
      return { previousSlideElement, nextSlideElement };
    };

    let { previousSlideElement, nextSlideElement } = findElements();

    if (!previousSlideElement || !nextSlideElement) {
      const timeoutId = setTimeout(() => {
        const retryResult = findElements();
        previousSlideElement = retryResult.previousSlideElement;
        nextSlideElement = retryResult.nextSlideElement;
        this.setupCarouselClickListeners(previousSlideElement, nextSlideElement);
      }, 200);
      this.updateSectionTimeoutIds.push(timeoutId);
      return;
    }

    this.setupCarouselClickListeners(previousSlideElement, nextSlideElement);
  }

  private setupCarouselClickListeners(prevElement: Element | null, nextElement: Element | null) {
    if (prevElement) {
      this.renderer.listen(prevElement, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.carouselList.prev();
      });
    }

    if (nextElement) {
      this.renderer.listen(nextElement, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.carouselList.next();
      });
    }
  }

  ngOnDestroy(): void {
    // Clear all timeouts to prevent memory leaks
    this.updateSectionTimeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
    this.updateSectionTimeoutIds = [];
  }
}

